'use client';

import { FC, useEffect, useRef, useState } from 'react';
import { runtimeConfig } from '@/lib/runtimeConfig';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

/* ── Attribute data ─────────────────────────────────── */

export const themeAttributes = [
  {
    name: 'member-unique-code',
    description: 'Eindeutiger Bezeichner des Kunden',
    example: '"wlas"',
  },
  {
    name: 'theme-primary',
    description: 'Primäre Akzentfarbe',
    example: '"#dc1c2e"',
  },
  {
    name: 'theme-link',
    description: 'Linkfarbe',
    example: '"#dc1c2e"',
  },
  {
    name: 'theme-font',
    description: 'Schriftfamilie (Standard: Urbanist; Montserrat eingebaut)',
    example: '"Montserrat"',
  },
  {
    name: 'theme-radius',
    description: 'Eckenradius',
    example: '"4px"',
  },
];

export const behaviorAttributes = [
  {
    name: 'initial-tabs',
    description: 'Startzeigeansicht: TILE (Kachelraster) oder LIST (Liste); Standard: TILE',
    example: '"LIST"',
  },
  {
    name: 'show-nav',
    description: 'Navigationsleiste (Kauf / Miete) anzeigen (true/false)',
    example: '"false"',
  },
  {
    name: 'show-breadcrumb',
    description: 'Breadcrumb-Navigation anzeigen (true/false)',
    example: '"false"',
  },
  {
    name: 'show-broker-info',
    description: 'Maklerinfo im Inserat anzeigen (true/false)',
    example: '"true"',
  },
  {
    name: 'detail-as-modal',
    description: 'Detailansicht als Modal öffnen statt Seitennavigation (true/false)',
    example: '"true"',
  },
  {
    name: 'show-page-size',
    description: 'Auswahl für Einträge pro Seite anzeigen (true/false)',
    example: '"false"',
  },
  {
    name: 'hide-pagination',
    description: 'Seitennavigation (Paginierung) ausblenden (true/false)',
    example: '"true"',
  },
  {
    name: 'hide-login-button',
    description: 'Login-Button ausblenden (true/false)',
    example: '"true"',
  },
  {
    name: 'hide-toolbar',
    description: 'Toolbar (Sortierung, Ansichtsumschalter) ausblenden (true/false)',
    example: '"true"',
  },
  {
    name: 'collapse-on-empty',
    description: 'Widget ausblenden, wenn keine Ergebnisse vorhanden (true/false)',
    example: '"true"',
  },
  {
    name: 'mini-slider',
    description:
      'Kompakter Horizontal-Slider statt vollständiger Liste — blendet Toolbar, Paginierung, Filter und Ansichtsumschalter aus (true/false)',
    example: '"true"',
  },
  {
    name: 'transparent-bg',
    description: 'Transparenter Hintergrund des Widgets (true/false)',
    example: '"true"',
  },
  {
    name: 'default-page-size',
    description: 'Standard-Einträge pro Seite: 6, 12, 24 oder 48',
    example: '"6"',
  },
  {
    name: 'exclude-office-id',
    description: 'Büro-ID, dessen Objekte aus der Ergebnisliste ausgeschlossen werden',
    example: '"42"',
  },
];

export const filterAttributes = [
  {
    name: 'pr_offerType',
    description: 'Angebotstyp: SELL oder RENT',
    example: '?pr_offerType=RENT',
  },
  {
    name: 'pr_keywords',
    description: 'Freitextsuche',
    example: '?pr_keywords=Wien',
  },
  {
    name: 'pr_land',
    description: 'Standortcodes (JSON-Array)',
    example: '?pr_land=["AT.9"]',
  },
  {
    name: 'pr_propertyType',
    description: 'Immobilientyp-Codes (JSON-Array)',
    example: '?pr_propertyType=["27","8"]',
  },
  {
    name: 'pr_areaMin / pr_areaMax',
    description: 'Fläche in m² (min/max)',
    example: '?pr_areaMin=50&pr_areaMax=200',
  },
  {
    name: 'pr_roomsMin / pr_roomsMax',
    description: 'Zimmeranzahl (min/max)',
    example: '?pr_roomsMin=2&pr_roomsMax=5',
  },
  {
    name: 'pr_sellPriceMin / pr_sellPriceMax',
    description: 'Kaufpreis (min/max)',
    example: '?pr_sellPriceMin=100000',
  },
  {
    name: 'pr_rentPriceMin / pr_rentPriceMax',
    description: 'Mietpreis (min/max)',
    example: '?pr_rentPriceMin=500',
  },
  {
    name: 'pr_hwbClass',
    description: 'Heizwärmebedarf-Klassen (JSON-Array)',
    example: '?pr_hwbClass=["A","B"]',
  },
  {
    name: 'pr_fgeeClass',
    description: 'Gesamtenergieeffizienz-Klassen (JSON-Array)',
    example: '?pr_fgeeClass=["A","B"]',
  },
  {
    name: 'pr_object_id',
    description: 'Objekt-ID für Detailansicht',
    example: '?pr_object_id=123456',
  },
];

export const paginationAttributes = [
  {
    name: 'pr_page',
    description: 'Aktuelle Seitennummer (ab 1)',
    example: '?pr_page=2',
  },
  {
    name: 'pr_perPage',
    description: 'Einträge pro Seite: 6, 12, 24 oder 48',
    example: '?pr_perPage=24',
  },
  {
    name: 'pr_tabs',
    description: 'Ansichtsmodus: TILE (Kacheln) oder Liste',
    example: '?pr_tabs=TILE',
  },
  {
    name: 'pr_sorting',
    description:
      'Sortierung: MODIFICATION_DATE, CREATION_DATE, PRICE_ASC, PRICE_DESC, AREA_ASC, AREA_DESC, POSTCODE_ASC, POSTCODE_DESC',
    example: '?pr_sorting=PRICE_ASC',
  },
];

/* ── AttributeTable ──────────────────────────────────── */

const tableCellSx = { fontSize: '0.85rem', py: 1.5 };

const AttributeTable: FC<{
  title: string;
  rows: { name: string; description: string; example: string }[];
}> = ({ title, rows }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
      {title}
    </Typography>
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ ...tableCellSx, fontWeight: 600 }}>Attribut</TableCell>
            <TableCell sx={{ ...tableCellSx, fontWeight: 600 }}>Beschreibung</TableCell>
            <TableCell sx={{ ...tableCellSx, fontWeight: 600 }}>Beispiel</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.name}>
              <TableCell sx={tableCellSx}>
                <code>{row.name}</code>
              </TableCell>
              <TableCell sx={tableCellSx}>{row.description}</TableCell>
              <TableCell sx={tableCellSx}>
                <code>{row.example}</code>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

/* ── DemoWidget ──────────────────────────────────────── */

export const DemoWidget: FC<{ attrs: Record<string, string> }> = ({ attrs }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const old = container.querySelector('alleimmobilien-wlas-widget');
    if (old) old.remove();

    const el = document.createElement('alleimmobilien-wlas-widget');
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    container.appendChild(el);
    // attrs is passed as a static object literal from each demo screen — no need to re-run on change
  }, []);

  return <div ref={containerRef} />;
};

/* ── SnippetBlock ────────────────────────────────────── */

export const SnippetBlock: FC<{ snippetBody: string; docsUrl?: string }> = ({
  snippetBody,
  docsUrl,
}) => {
  const [copied, setCopied] = useState(false);
  const snippet = `<script src="${runtimeConfig.WLAS_BASE_URL}alleimmobilien-wlas-widget.js" defer></script>${snippetBody}`;

  const handleCopy = () => {
    void navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Integrations-Code
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {docsUrl && (
            <Box
              component="a"
              href={docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.75,
                fontSize: '0.8rem',
                fontFamily: 'inherit',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                textDecoration: 'none',
                color: 'text.secondary',
                '&:hover': { borderColor: 'grey.500', color: 'text.primary' },
              }}
            >
              Dokumentation ↗
            </Box>
          )}
          <Box
            component="button"
            onClick={handleCopy}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              fontSize: '0.8rem',
              fontFamily: 'inherit',
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
              background: 'none',
              cursor: 'pointer',
              color: copied ? 'success.main' : 'text.secondary',
              '&:hover': { borderColor: 'grey.500', color: 'text.primary' },
            }}
          >
            {copied ? 'Kopiert ✓' : 'Kopieren ⎘'}
          </Box>
        </Box>
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2.5,
          background: '#1e1e1e',
          borderRadius: 2,
          fontSize: '0.8rem',
          lineHeight: 1.7,
          color: '#d4d4d4',
          fontFamily: '"Fira Code", "Cascadia Code", monospace',
          overflowX: 'auto',
          whiteSpace: 'pre',
        }}
      >
        {snippet}
      </Box>
    </Box>
  );
};

/* ── WidgetDocsSection ───────────────────────────────── */

export const WidgetDocsSection: FC = () => (
  <Box sx={{ mt: 6 }}>
    <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
      Widget-Anpassungsparameter
    </Typography>

    <AttributeTable title="Styling (HTML-Attribute)" rows={themeAttributes} />
    <AttributeTable title="Verhalten (HTML-Attribute)" rows={behaviorAttributes} />
    <AttributeTable title="Filter (URL-Parameter)" rows={filterAttributes} />
    <AttributeTable
      title="Paginierung, Ansicht & Sortierung (URL-Parameter)"
      rows={paginationAttributes}
    />

    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        Die genauen Werte für übergebene Codes (z.&nbsp;B. Standort-, Immobilientyp- und
        Energieklassen-Codes) sind in der vollständigen Dokumentation beschrieben.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Mit der Einführung des Admin-Panels können alle diese Parameter direkt dort konfiguriert
        werden — ohne sie manuell über URL-Parameter übergeben zu müssen.
      </Typography>
    </Box>
  </Box>
);
